import type { TFunction } from "i18next";
import type { FusionItemDef, FusionMenuConfig } from "~/components/dock/FusionMenu";

import { ApplicationEnum } from "~/@types/enums";
import { LearningModule } from "~/gql/graphqlBase.generated";
import { isTenantSettingsPageId } from "~/helpers/canOpenTenantSettings";
import resolveFulfillReportsMenuItems from "~fulfill/dock/helpers/resolveFulfillReportsMenuItems";

const createFulfillSections = (
	t: TFunction<"common">,
	fulfillReportItems: FusionItemDef[],
	fulfillReportsError: boolean,
	fulfillReportsLoading: boolean
): FusionMenuConfig["sections"] => [
	{
		groups: [
			{
				items: [
					{ icon: "dashboard", label: t("dock.pages.fulfill.dashboard"), pageId: "dashboard" },
					{ icon: "insights", label: t("dock.pages.fulfill.salesPerformance"), pageId: "sales-performance" },
					{ icon: "payments", label: t("dock.pages.fulfill.cashCollections"), pageId: "cash-collections" }
				]
			}
		],
		icon: "dashboard",
		id: "dashboard",
		label: t("dock.pages.fulfill.dashboard")
	},
	{
		groups: [
			{
				items: [{ icon: "shopping_cart", pageId: "orders" }]
			}
		],
		icon: "shopping_cart",
		id: "orders",
		label: t("dock.pages.fulfill.orders")
	},
	{
		groups: [
			{
				items: [
					{ icon: "business", label: t("dock.pages.fulfill.customersList"), pageId: "customers" },
					{ icon: "route", label: t("dock.pages.fulfill.customersRoutes"), pageId: "customers-routes" },
					{ icon: "groups", label: t("dock.pages.fulfill.customersGroups"), pageId: "customers-groups" }
				]
			}
		],
		icon: "business",
		id: "customers",
		label: t("dock.pages.fulfill.customers")
	},
	{
		groups: [
			{
				items: [
					{ icon: "sell", label: t("dock.pages.fulfill.salesPricing"), pageId: "sales-pricing" },
					{ icon: "receipt", label: t("dock.pages.fulfill.salesTerms"), pageId: "sales-terms" },
					{ icon: "category", label: t("dock.pages.fulfill.salesOrderKinds"), pageId: "sales-order-kinds" },
					{
						icon: "request_quote",
						label: t("dock.pages.fulfill.salesStatements"),
						pageId: "sales-statements"
					},
					{ icon: "receipt_long", label: t("dock.pages.fulfill.salesInvoices"), pageId: "sales-invoices" },
					{
						icon: "account_balance",
						label: t("dock.pages.fulfill.salesReceivables"),
						pageId: "sales-receivables"
					},
					{
						icon: "payments",
						label: t("dock.pages.fulfill.salesReceivePayments"),
						pageId: "sales-receive-payments"
					}
				]
			}
		],
		icon: "sell",
		id: "sales",
		label: t("dock.pages.fulfill.sectionSales")
	},
	{
		groups: [
			{
				items: [
					{ icon: "list_alt", label: t("dock.pages.fulfill.productsItems"), pageId: "products-items" },
					{ icon: "category", label: t("dock.pages.fulfill.productsCategories"), pageId: "products-categories" },
					{ icon: "inventory_2", label: t("dock.pages.fulfill.productsHardGoods"), pageId: "products-hard-goods" },
					{ icon: "widgets", label: t("dock.pages.fulfill.productsKits"), pageId: "products-kits" }
				],
				label: t("dock.pages.fulfill.groupCatalog")
			},
			{
				items: [
					{ icon: "package_2", label: t("dock.pages.fulfill.productsContainers"), pageId: "products-containers" },
					{
						icon: "local_shipping",
						label: t("dock.pages.fulfill.productsShippingConfig"),
						pageId: "products-shipping-config"
					}
				],
				label: t("dock.pages.fulfill.groupMaintenance")
			}
		],
		icon: "widgets",
		id: "products",
		label: t("dock.pages.fulfill.sectionProducts")
	},
	{
		groups: [
			{
				items: [
					{ icon: "storefront", label: t("dock.pages.fulfill.purchasingVendors"), pageId: "purchasing-vendors" },
					{
						icon: "receipt_long",
						label: t("dock.pages.fulfill.purchasingOrders"),
						pageId: "purchasing-orders"
					}
				]
			}
		],
		icon: "local_shipping",
		id: "purchasing",
		label: t("dock.pages.fulfill.sectionPurchasing")
	},
	{
		groups: [{ items: [{ icon: "location_pin", pageId: "locations" }] }],
		icon: "location_pin",
		id: "locations",
		label: t("dock.pages.fulfill.locations")
	},
	{
		groups: [{ items: [{ icon: "inventory_2", pageId: "inventory-stock" }] }],
		icon: "inventory_2",
		id: "inventory",
		label: t("dock.pages.fulfill.inventoryStock")
	},
	{
		groups: [
			{
				items: [
					{ icon: "route", label: t("dock.pages.fulfill.logisticsPlanning"), pageId: "logistics-planning" },
					{ icon: "local_shipping", label: t("dock.pages.fulfill.logisticsDeliveries"), pageId: "logistics-deliveries" }
				],
				label: t("dock.pages.fulfill.groupRouting")
			},
			{
				items: [
					{ icon: "alt_route", label: t("dock.pages.fulfill.logisticsRouting"), pageId: "logistics-routing" },
					{ icon: "domain", label: t("dock.pages.fulfill.logisticsCarriers"), pageId: "logistics-carriers" },
					{ icon: "garage", label: t("dock.pages.fulfill.logisticsFleet"), pageId: "logistics-fleet" },
					{ icon: "warehouse", label: t("dock.pages.fulfill.logisticsDocks"), pageId: "logistics-docks" }
				],
				label: t("dock.pages.fulfill.groupSetup")
			}
		],
		icon: "local_shipping",
		id: "logistics",
		label: t("dock.pages.fulfill.sectionLogistics")
	},
	{
		groups: [
			{
				items: [
					{ icon: "bolt", label: t("dock.pages.fulfill.portalQuickOrders"), pageId: "portal-quick-orders" },
					{ icon: "palette", label: t("dock.pages.fulfill.portalBranding"), pageId: "portal-branding" },
					{ icon: "settings", label: t("dock.pages.fulfill.portalSettings"), pageId: "portal-settings" }
				]
			}
		],
		icon: "storefront",
		id: "portal",
		label: t("dock.pages.fulfill.sectionPortal")
	},
	{
		groups: [
			{ items: resolveFulfillReportsMenuItems(fulfillReportItems, fulfillReportsError, fulfillReportsLoading, t) }
		],
		icon: "assessment",
		id: "reports",
		label: t("dock.pages.reports.section")
	},
	{
		groups: [
			{
				items: [
					{ icon: "tune", label: t("dock.pages.fulfill.settingsGeneral"), pageId: "settings-general" },
					{ icon: "receipt_long", label: t("dock.pages.fulfill.settingsSalesTax"), pageId: "settings-sales-tax" },
					{
						icon: "label",
						label: t("dock.pages.fulfill.settingsCharacteristics"),
						pageId: "settings-characteristics"
					}
				]
			}
		],
		icon: "settings",
		id: "settings",
		label: t("dock.pages.fulfill.sectionSettings")
	}
];

const withCommissionsItem = (
	t: TFunction<"common">,
	sections: FusionMenuConfig["sections"],
	canManageCommissions: boolean
): FusionMenuConfig["sections"] => {
	if (!canManageCommissions) return sections;
	return sections.map((section) =>
		section.id === "settings"
			? {
					...section,
					groups: [
						{
							items: [
								...section.groups[0].items,
								{
									icon: "percent",
									label: t("dock.pages.fulfill.settingsCommissions"),
									pageId: "settings-commissions"
								}
							]
						}
					]
				}
			: section
	);
};

const withoutKitsItem = (
	sections: FusionMenuConfig["sections"],
	kitsEnabled: boolean
): FusionMenuConfig["sections"] => {
	if (kitsEnabled) return sections;
	return sections.map((section) => ({
		...section,
		groups: section.groups.map((group) => ({
			...group,
			items: group.items.filter((item) => item.pageId !== "products-kits")
		}))
	}));
};

const withoutLogisticsSection = (
	sections: FusionMenuConfig["sections"],
	logisticsEnabled: boolean
): FusionMenuConfig["sections"] => {
	if (logisticsEnabled) return sections;
	return sections.filter((section) => section.id !== "logistics");
};

const createFulfillFusionConfig = (
	t: TFunction<"common">,
	dashboardEnabled: boolean,
	canManageCommissions: boolean,
	kitsEnabled: boolean,
	logisticsEnabled: boolean,
	fulfillReportItems: FusionItemDef[] = [],
	fulfillReportsError = false,
	fulfillReportsLoading = false,
	canManageSettings = false
): FusionMenuConfig => {
	const fulfillSections = createFulfillSections(t, fulfillReportItems, fulfillReportsError, fulfillReportsLoading).map(
		(section) => ({
			...section,
			groups: section.groups.map((group) => ({
				...group,
				items: group.items.filter((item) => canManageSettings || !isTenantSettingsPageId(item.pageId))
			}))
		})
	);
	return {
		defaultQuickActionKeys: ["orders?action=new", "customers?action=new"],
		learningModule: LearningModule.Fulfill,
		logo: "/logos/fulfill.svg",
		module: ApplicationEnum.FULFILL,
		quickActions: [
			{ action: "new", icon: "shopping_cart", label: t("dock.quickActionLabels.fulfill.newOrder"), pageId: "orders" },
			{
				action: "new",
				icon: "business",
				label: t("dock.quickActionLabels.fulfill.addCustomer"),
				pageId: "customers"
			},
			{ icon: "list_alt", label: t("dock.quickActionLabels.fulfill.items"), pageId: "products-items" }
		],
		sections: withCommissionsItem(
			t,
			withoutLogisticsSection(
				withoutKitsItem(
					dashboardEnabled ? fulfillSections : fulfillSections.filter((section) => section.id !== "dashboard"),
					kitsEnabled
				),
				logisticsEnabled
			),
			canManageCommissions
		)
	};
};

export default createFulfillFusionConfig;
